import { styled, Tab, Tabs as MUITabs } from '@mui/material';
import { useViewport } from 'contexts/viewport';
import { omit } from 'lodash';
import { useCallback } from 'react';
import Box from 'shell/components/Box';
import TabPanel from 'shell/components/TabPanel';
import { useDraggableScroll } from 'shell/hooks';

const interactiveTabStates = ({ hover, pressed }) => ({
    '&:not([aria-disabled="true"]):not(.Mui-disabled)': {
        '&:hover': hover,
        '&:active': pressed,
    },
});

const underlineTabStyles = (theme) => ({
    margin: 0,
    width: '100%',
    borderRadius: 0,
    background: 'transparent',
    borderBottom: `1px solid ${theme.palette.ensightful.neutral[300]}`,
    '& .MuiTabs-flexContainer': {
        height: 'auto',
        padding: 0,
        gap: 0,
    },
    '& .MuiTab-root': {
        borderRadius: 0,
        height: 'auto',
        minHeight: 34,
        minWidth: 130,
        padding: theme.spacing(2, 3),
        fontSize: '0.75rem',
        fontWeight: theme.typography.p3.fontWeight.medium,
        lineHeight: `${theme.typography.getLineHeight('p3', 'large')}rem`,
        whiteSpace: 'nowrap',
        borderBottom: '2px solid transparent',
        background: 'none',
        transition: theme.transitions.create(['background-color', 'border-color', 'color']),
        ...interactiveTabStates({
            hover: {
                color: theme.palette.ensightful.primary[500],
            },
            pressed: {
                background: 'transparent',
                color: theme.palette.ensightful.primary[900],
            },
        }),
        '&&.Mui-focusVisible': {
            outline: `3px solid ${theme.palette.ensightful.secondary[400]}`,
            outlineOffset: '-3px',
        },
        '&.Mui-selected': {
            borderRadius: 0,
            color: theme.palette.ensightful.neutral[900],
            borderBottomColor: theme.palette.ensightful.primary[500],
            ...interactiveTabStates({
                hover: {
                    color: theme.palette.ensightful.primary[900],
                },
                pressed: {
                    color: theme.palette.ensightful.primary[900],
                },
            }),
        },
        '&[aria-disabled="true"], &.Mui-disabled': {
            background: theme.palette.ensightful.neutral[300],
            borderBottomColor: 'transparent',
            color: theme.palette.ensightful.neutral[500],
            cursor: 'not-allowed',
            opacity: 1,
        },
    },
    [theme.breakpoints.down('tablet')]: {
        '& .MuiTabs-scroller': {
            touchAction: 'pan-x',
        },
        '& .MuiTabs-scrollButtons.Mui-disabled': {
            display: 'none',
        },
    },
});

const CustomTabs = styled(MUITabs, {
    shouldForwardProp: (prop) => !['$fullWidth', '$isDragging'].includes(prop),
})(({ theme, $fullWidth, $isDragging }) => {
    return {
        margin: '-3px',
        ...($fullWidth && {
            width: '100%',
        }),
        ...($isDragging && {
            cursor: 'grabbing',
        }),
        minHeight: 'unset',
        background: theme.palette.ensightful.neutral[200],
        borderRadius: '40px',
        '& .MuiTabs-flexContainer': {
            height: 'calc(2.125rem + 6px)',
            padding: '3px',
            gap: '4px',
            ...($fullWidth && {
                '& > :first-of-type': {
                    marginInlineStart: 'auto',
                },
                '& > :last-of-type': {
                    marginInlineEnd: 'auto',
                },
            }),
        },
        '& .MuiTabs-indicator': {
            display: 'none',
        },
    };
});

const CustomTab = styled(Tab)(({ device, $disabled, $fullWidth, theme }) => {
    const size = {
        mobile: 'small',
        tablet: 'large',
        desktop: 'large',
    }[device];

    return {
        flex: $fullWidth ? '0 1 auto' : 1,
        maxWidth: 'unset',
        minHeight: 'unset',
        height: '2.125rem',
        padding: theme.spacing(2, 1),
        borderRadius: '40px',
        fontSize: `${theme.typography.p2.fontSize[size]}rem`,
        fontWeight: theme.typography.p2.fontWeight.medium,
        letterSpacing: `${theme.typography.p2.letterSpacing[size]}rem`,
        lineHeight: `${theme.typography.getLineHeight('p2', size)}rem`,
        color: theme.palette.ensightful.neutral[700],
        transition: theme.transitions.default,
        '&:hover': {
            background: theme.palette.ensightful.shades[0],
        },
        '&:active': {
            background: theme.palette.ensightful.neutral[400],
        },
        '&&.Mui-focusVisible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '-5px',
        },
        '&.Mui-selected': {
            background: theme.palette.ensightful.shades[0],
            color: theme.palette.ensightful.primary[500],
            borderRadius: '40px',
            padding: '3px',

            '&:hover': {
                color: theme.palette.ensightful.neutral[700],
            },
        },
        ...($disabled && {
            background: theme.palette.ensightful.neutral[300],
            color: theme.palette.ensightful.neutral[500],
            pointerEvents: 'none',
        }),
        ...(device === 'mobile' && {
            minWidth: 0,
        }),
    };
});

const Tabs = ({
    options,
    selected,
    onChange,
    appearance = 'pill',
    fullWidth = false,
    keepMounted = false,
    dragToScroll = false,
    sx,
    ...rest
}) => {
    if (!rest['aria-label'] && !rest['aria-labelledby']) {
        throw new Error('Shell: One of aria-label or aria-labelledby is required in component Tabs.');
    }

    const { device } = useViewport();
    const { ref: draggableScrollRef, events: draggableScrollEvents, isDragging } = useDraggableScroll();
    const setTabsRootRef = useCallback(
        (node) => {
            draggableScrollRef.current = node?.querySelector('.MuiTabs-scroller') || null;
        },
        [draggableScrollRef]
    );

    return (
        <Box {...omit(rest, ['aria-label'])} sx={fullWidth ? [{ width: '100%' }, sx] : sx}>
            <CustomTabs
                $fullWidth={fullWidth}
                $isDragging={isDragging}
                ref={setTabsRootRef}
                variant={appearance === 'underline' ? 'scrollable' : 'standard'}
                scrollButtons={appearance === 'underline' ? 'auto' : false}
                allowScrollButtonsMobile
                sx={appearance === 'underline' ? underlineTabStyles : undefined}
                aria-label={rest['aria-label']}
                aria-labelledby={rest['aria-labelledby']}
                value={options.findIndex(({ key }) => key === selected)}
                {...(dragToScroll ? draggableScrollEvents : {})}
                onChange={(_, index) => {
                    if (!options[index].disabled) onChange(options[index].key);
                }}
            >
                {options.map(({ key, value, disabled }) => (
                    <CustomTab
                        id={`tab-${key}`}
                        key={key}
                        aria-controls={`tabpanel-${key}`}
                        aria-disabled={disabled}
                        device={device}
                        $disabled={disabled}
                        $fullWidth={fullWidth}
                        label={value}
                        onKeyDown={(e) => {
                            if (disabled) e.preventDefault();
                        }}
                    />
                ))}
            </CustomTabs>
            {options.map(({ key, content }) => (
                <TabPanel key={key} direction={'column'} $key={key} selected={selected} keepMounted={keepMounted}>
                    {content}
                </TabPanel>
            ))}
        </Box>
    );
};

export default Tabs;
